import { FC } from 'react';
import { ChartResponse } from '../types';
import { Tooltip as UITooltip } from '@/components/ui';
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    Tooltip
} from 'recharts';
import {
    Target, Activity, BarChart3, Zap
} from 'lucide-react';

const SOURCE_COLORS: Record<string, string> = {
    'CSV Import': '#6366f1',
    'Social Post': '#f59e0b',
    'Landing Page': '#10b981',
    'Direct/Other': '#94a3b8'
};

const PLATFORM_COLORS: Record<string, string> = {
    linkedin: '#0077b5',
    facebook: '#1877f2',
    instagram: '#e4405f',
    twitter: '#1da1f2'
};

interface LeadSourceAnalyticsProps {
    chartData: ChartResponse | null;
}

export const LeadSourceAnalytics: FC<LeadSourceAnalyticsProps> = ({ chartData }) => {
    if (!chartData?.leadSources) return null;

    const totalLeads = chartData.leadSources.reduce((a, b) => a + b.total, 0);

    const sourceData = chartData.leadSources.map(s => ({
        name: s.source,
        value: s.total,
        rate: s.rate,
        color: SOURCE_COLORS[s.source] || '#cbd5e1'
    }));

    const platformStats = [
        { id: 'linkedin', name: 'LinkedIn', color: PLATFORM_COLORS.linkedin },
        { id: 'facebook', name: 'Facebook', color: PLATFORM_COLORS.facebook },
        { id: 'instagram', name: 'Instagram', color: PLATFORM_COLORS.instagram },
        { id: 'twitter', name: 'Twitter', color: PLATFORM_COLORS.twitter }
    ].map(p => {
        const leads = chartData.leadsByPlatform?.find(lp => lp.platform.toLowerCase() === p.id.toLowerCase())?.count || 0;
        const clicks = (chartData.platformClicks as Record<string, number>)?.[p.id] || 0;
        const conv = clicks > 0 ? (leads / clicks) * 100 : 0;
        return { ...p, leads, clicks, conv };
    }).filter(p => p.clicks > 0 || p.leads > 0);

    const maxClicks = Math.max(...platformStats.map(p => p.clicks), 1);
    const maxLeads = Math.max(...platformStats.map(p => p.leads), 1);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 col-span-1 lg:col-span-2">

            {/* 1. RESTORED: Lead Acquisition Sources (Untouched as requested) */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm h-[420px] flex flex-col">
                <div className="flex items-center justify-between mb-6">
                    <UITooltip content="A look at where your leads are coming from and how well each source is performing." position="top">
                        <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-indigo-500" />
                            <h3 className="text-sm font-bold text-slate-800">Lead Acquisition Sources</h3>
                        </div>
                    </UITooltip>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center flex-1">
                    <div className="min-h-[180px] h-full relative">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={sourceData} innerRadius={50} outerRadius={70} paddingAngle={5} dataKey="value">
                                    {sourceData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-xl font-bold text-slate-900">{totalLeads}</span>
                            <span className="text-[9px] text-slate-400 uppercase font-bold">Leads</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {sourceData.map((item) => (
                            <div key={item.name} className="flex flex-col gap-1">
                                <div className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                                        <span className="font-semibold text-slate-700">{item.name}</span>
                                    </div>
                                    <span className="font-bold text-slate-900">{item.value}</span>
                                </div>
                                <div className="flex items-center justify-between pl-4">
                                    <span className="text-[9px] text-slate-400 uppercase tracking-tighter">Conversion Rate</span>
                                    <span className="text-[10px] font-bold text-emerald-600">{item.rate.toFixed(1)}%</span>
                                </div>
                                <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mt-1">
                                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${item.rate}%`, backgroundColor: item.color }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 2. CREATIVE: Conversion Pipeline Scorecard (Dynamic Data Only) */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm h-[420px] flex flex-col">
                <div className="flex items-center justify-between mb-8">
                    <UITooltip content="How each platform performs at turning views and clicks into new leads." position="top">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-amber-50 rounded-lg">
                                <Activity className="w-4 h-4 text-amber-600" />
                            </div>
                            <h3 className="text-sm font-bold text-slate-800">Platform Performance Pipeline</h3>
                        </div>
                    </UITooltip>
                    <BarChart3 className="w-4 h-4 text-slate-200" />
                </div>

                {platformStats.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center opacity-30 grayscale py-10">
                        <Zap className="w-8 h-8 text-slate-300 mb-2" />
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">No Activity Tracked</p>
                    </div>
                ) : (
                    <div className="space-y-6 flex-1">
                        {platformStats.map((p) => (
                            <div key={p.id} className="relative group">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-4 rounded-full" style={{ backgroundColor: p.color }} />
                                        <span className="text-[11px] font-black text-slate-800 uppercase tracking-tight">{p.name}</span>
                                    </div>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xs font-black text-indigo-600">{p.conv.toFixed(1)}%</span>
                                        <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Success Rate</span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 items-center">
                                    {/* Clicks Pipeline (Normalized to Max Clicks) */}
                                    <div className="flex flex-col gap-1">
                                        <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden relative">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-slate-200 rounded-full transition-all duration-1000"
                                                style={{ width: `${(p.clicks / maxClicks) * 100}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between items-center text-[9px] font-bold text-slate-400">
                                            <span className="uppercase tracking-tighter">Views / Clicks</span>
                                            <span className="text-slate-600 font-black">{p.clicks} <span className="text-[8px] opacity-70">Clicks</span></span>
                                        </div>
                                    </div>

                                    {/* Leads Pipeline (Normalized to Max Leads) */}
                                    <div className="flex flex-col gap-1 border-l border-slate-50 pl-4">
                                        <div className="h-1.5 w-full bg-indigo-50/50 rounded-full overflow-hidden relative">
                                            <div
                                                className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 shadow-[0_0_8px_rgba(99,102,241,0.2)]"
                                                style={{ width: `${(p.leads / maxLeads) * 100}%`, backgroundColor: p.color }}
                                            />
                                        </div>
                                        <div className="flex justify-between items-center text-[9px] font-bold text-slate-400">
                                            <span className="uppercase tracking-tighter">Leads Found</span>
                                            <span className="text-indigo-600 font-black">{p.leads} <span className="text-[8px] opacity-70">Leads</span></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-6 pt-4 border-t border-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                        <Zap className="w-3.5 h-3.5 text-amber-500" />
                        Benchmark Metric
                    </div>
                    <span className="text-[10px] font-black text-slate-400 italic">
                        Avg Success: {(platformStats.reduce((a, b) => a + b.conv, 0) / (platformStats.length || 1)).toFixed(1)}%
                    </span>
                </div>
            </div>
        </div>
    );
};
